import type { ReactNode } from 'react';
import { JourneyHeaderAuthLink } from '../features/journeys/JourneyHeaderAuthLink.js';
import {
  CUSTOMER_ACCOUNT_HEADER_CENTER_SLOT_CLASS,
  CUSTOMER_ACCOUNT_HEADER_END_SLOT_CLASS,
  CUSTOMER_ACCOUNT_HEADER_INNER_CLASS,
  CUSTOMER_ACCOUNT_HEADER_START_SLOT_CLASS,
  CUSTOMER_TOP_BAR_ACTIONS_CLASS,
  CUSTOMER_TOP_BAR_FLOW_SPACER_CLASS,
  CUSTOMER_TOP_BAR_SHELL_HEADER_CLASS,
  CUSTOMER_TOP_BAR_SUBTITLE_CLASS,
  CUSTOMER_TOP_BAR_TITLE_CLASS,
} from './customerTopBarStyles.js';

interface CustomerTopBarProps {
  readonly title?: string;
  readonly subtitle?: string;
  readonly context?: 'POST_KIOSK_PRODUCT' | 'POST_KIOSK_DONATION' | 'PHONE_FIRST_PRODUCT' | 'PHONE_FIRST_DONATION';
  readonly forceShopTestId?: boolean;
  readonly trailing?: ReactNode;
}

/**
 * Standalone fixed chrome for journeys OUTSIDE CustomerAppShell.
 *
 * Under CustomerAppShell, JourneyShell already skips this TopBar
 * (`useCustomerShellChrome` → in-shell path renders title/content only).
 * Stage 3 shell header is ← · Org · Cart · Settings · Profile (center
 * title/place); language · appearance prefs live on the Settings sheet —
 * not here / not in-header / not on Profile sheet.
 * Auth remains JourneyHeaderAuthLink (profile / sign-in).
 */
export function CustomerTopBar({
  title,
  subtitle,
  context,
  forceShopTestId = false,
  trailing,
}: CustomerTopBarProps): JSX.Element {
  const hasTitle = title !== undefined && title.length > 0;
  const hasSubtitle = subtitle !== undefined && subtitle.length > 0;
  const auth =
    trailing ?? (
      <JourneyHeaderAuthLink context={context} forceShopTestId={forceShopTestId} />
    );

  if (hasTitle || hasSubtitle) {
    return (
      <>
        <header className={CUSTOMER_TOP_BAR_SHELL_HEADER_CLASS}>
          <div className={CUSTOMER_ACCOUNT_HEADER_INNER_CLASS}>
            <div className="min-w-0 flex-1">
              {hasTitle ? <h1 className={CUSTOMER_TOP_BAR_TITLE_CLASS}>{title}</h1> : null}
              {hasSubtitle ? <p className={CUSTOMER_TOP_BAR_SUBTITLE_CLASS}>{subtitle}</p> : null}
            </div>
            <div className={CUSTOMER_TOP_BAR_ACTIONS_CLASS}>{auth}</div>
          </div>
        </header>
        <div aria-hidden="true" className={CUSTOMER_TOP_BAR_FLOW_SPACER_CLASS} />
      </>
    );
  }

  return (
    <>
      <header className={CUSTOMER_TOP_BAR_SHELL_HEADER_CLASS}>
        <div className={CUSTOMER_ACCOUNT_HEADER_INNER_CLASS}>
          <div className={CUSTOMER_ACCOUNT_HEADER_START_SLOT_CLASS} aria-hidden="true" />
          <div className={CUSTOMER_ACCOUNT_HEADER_CENTER_SLOT_CLASS} aria-hidden="true" />
          <div className={CUSTOMER_ACCOUNT_HEADER_END_SLOT_CLASS}>{auth}</div>
        </div>
      </header>
      <div aria-hidden="true" className={CUSTOMER_TOP_BAR_FLOW_SPACER_CLASS} />
    </>
  );
}
